import { Component, computed, inject, input } from '@angular/core';
import { Store } from '@ngrx/store';
import { InnogyComponentRendering } from '@core/jss-models';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import {
  type RichTextField,
  type TextField,
  type ImageField,
  type LinkField,
  type ComponentFields,
  getChildPlaceholder,
} from '@sitecore-jss/sitecore-jss-angular';
import { hasBreadcrumbs } from '@core/jss-routing';
import { getAppStoreButton, getGooglePlayButton } from '@core/navigation-store';
import { HERO_TYPES, HeroComponent } from '@content/ui/hero';
import { type Hero, HERO_FIELDS } from '@content/ui/hero';

const HEADING_LINE1_FALLBACK: TextField = {
  value: 'HEADING LINE 1 IS REQUIRED',
};

@Component({
  selector: 'wl-hero-container',
  standalone: true,
  imports: [HeroComponent],
  templateUrl: './hero-container.component.html',
})
export class HeroContainerComponent {
  readonly #config = inject(ENVIRONMENT_CONFIG);
  readonly #store = inject(Store);

  readonly rendering = input.required<InnogyComponentRendering>();

  readonly hasBreadcrumbs = this.#store.selectSignal(hasBreadcrumbs);
  readonly appStoreButton = this.#store.selectSignal(getAppStoreButton);
  readonly googlePlayButton = this.#store.selectSignal(getGooglePlayButton);

  readonly hasAsset = computed(
    () =>
      this.rendering()?.componentName === HERO_TYPES.HERO_WITH_ASSET ||
      this.rendering()?.componentName === HERO_TYPES.HERO_WITH_APP_BUTTONS
  );
  readonly hasAppButtons = computed(
    () => this.rendering()?.componentName === HERO_TYPES.HERO_WITH_APP_BUTTONS
  );
  readonly hasWidget = computed(
    () => this.rendering()?.componentName === HERO_TYPES.HERO_WITH_WIDGET
  );

  readonly hasValidWidget = computed(() => {
    const rendering = this.rendering();
    return (
      this.hasWidget() &&
      getChildPlaceholder(rendering, 'hero-widget-dynamic').length > 0
    );
  });

  readonly hero = computed((): Hero => {
    const rendering = this.rendering();
    const fields = rendering.fields;

    if (!fields) {
      return {
        headingLine1: HEADING_LINE1_FALLBACK,
      };
    }

    const get = <T>(field: HERO_FIELDS): T | undefined =>
      this.getField<T>(fields, field);

    return {
      headingLine1:
        get<TextField>(HERO_FIELDS.HEADING_LINE1) ?? HEADING_LINE1_FALLBACK,
      headingLine2: get<TextField>(HERO_FIELDS.HEADING_LINE2),
      primaryButton: get<LinkField>(HERO_FIELDS.PRIMARY_BUTTON),
      secondaryButton: get<LinkField>(HERO_FIELDS.SECONDARY_BUTTON),
      paragraph: get<TextField>(HERO_FIELDS.PARAGRAPH),
      richText: get<RichTextField>(HERO_FIELDS.RICH_TEXT),
      bottomImage: get<ImageField>(HERO_FIELDS.BOTTOM_IMAGE),
      mainImage: this.hasValidWidget()
        ? undefined
        : get<ImageField>(HERO_FIELDS.MAIN_IMAGE),
      backgroundImage: get<ImageField>(HERO_FIELDS.BACKGROUND_IMAGE),
      widget: this.hasValidWidget() ? rendering : undefined,
      appStoreButton: this.hasAppButtons()
        ? (this.appStoreButton() ?? undefined)
        : undefined,
      googlePlayButton: this.hasAppButtons()
        ? (this.googlePlayButton() ?? undefined)
        : undefined,
    };
  });

  readonly enableEssentRebranding =
    this.#config.styles?.enableEssentRebranding || false;

  private getField<T>(
    fields: ComponentFields,
    field: HERO_FIELDS
  ): T | undefined {
    return fields[field] as T | undefined;
  }
}
